import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";

const formatMoney = (amount, currency, lang) => {
  try {
    return new Intl.NumberFormat(lang === "en" ? "en-US" : "fr-FR", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (_) {
    return `${amount} ${currency}`;
  }
};

const CampaignsSection = () => {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api
      .get("/campaigns?featured_only=true")
      .then(({ data }) => setItems(Array.isArray(data) ? data.slice(0, 3) : []))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  return (
    <section
      id="campaigns"
      className="py-24 md:py-32 bg-white border-t border-[#E5E7EB]"
      data-testid="home-campaigns-section"
    >
      <div className="container-narrow">
        <div className="max-w-2xl mb-12">
          <span className="eyebrow text-[#C5A059]" data-testid="home-campaigns-eyebrow">{t.homeCampaigns.eyebrow}</span>
          <span className="block w-16 h-px bg-[#C5A059] mt-4 mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#002B49] leading-[1.1] font-medium" data-testid="home-campaigns-title">
            {t.homeCampaigns.title}
          </h2>
          <p className="text-[#334155] text-base md:text-lg leading-relaxed mt-5">
            {t.homeCampaigns.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {items.map((c, i) => {
            const detailPath = lang === "en" ? `/campaigns/${c.slug}` : `/campagnes/${c.slug}`;
            return (
              <motion.article
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group bg-[#FDFBF7] rounded-2xl overflow-hidden border border-black/5 shadow-[0_8px_40px_-20px_rgba(0,43,73,0.15)] flex flex-col hover:-translate-y-1 transition-transform"
                data-testid={`home-campaign-card-${i}`}
              >
                <Link to={detailPath} className="block aspect-[16/10] overflow-hidden bg-[#F3F0EA]">
                  {c.image_url && (
                    <img
                      src={c.image_url}
                      alt={lang === "en" ? c.title_en : c.title_fr}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  )}
                </Link>
                <div className="p-5 md:p-6 flex-1 flex flex-col">
                  <h3 className="font-serif text-lg md:text-xl text-[#002B49] font-semibold leading-tight">
                    {lang === "en" ? c.title_en : c.title_fr}
                  </h3>

                  <div className="mt-4">
                    <div className="flex items-end justify-between text-xs mb-2">
                      <span className="font-semibold text-[#002B49] text-sm">
                        {formatMoney(c.raised_amount, c.currency, lang)}
                      </span>
                      <span className="text-[#64748B]">{c.percentage}%</span>
                    </div>
                    <div className="h-2 bg-[#F3F0EA] rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-[#002B49] to-[#C5A059] rounded-full"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${Math.min(c.percentage, 100)}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-[#64748B] mt-2">
                      <span>{t.homeCampaigns.of} {formatMoney(c.goal_amount, c.currency, lang)}</span>
                      <span className="inline-flex items-center gap-1"><Users size={11} /> {c.donor_count}</span>
                    </div>
                  </div>

                  <div className="mt-5 flex gap-2 items-center">
                    <Link
                      to={`/?campaign=${c.slug}#donate`}
                      className="flex-1 inline-flex items-center justify-center bg-[#002B49] hover:bg-[#001F35] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                      data-testid={`home-campaign-support-${i}`}
                    >
                      {t.homeCampaigns.support}
                    </Link>
                    <Link
                      to={detailPath}
                      className="inline-flex items-center text-sm font-medium text-[#002B49] hover:text-[#C5A059] transition-colors px-2"
                      aria-label="More details"
                      data-testid={`home-campaign-detail-${i}`}
                    >
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Link
            to={lang === "en" ? "/campaigns" : "/campagnes"}
            className="inline-flex items-center gap-2 text-[#002B49] font-medium hover:text-[#C5A059] transition-colors border-b border-[#C5A059]/40 hover:border-[#C5A059] pb-1"
            data-testid="home-campaigns-view-all"
          >
            {t.homeCampaigns.viewAll}
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CampaignsSection;
